import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { sanitizeString } from '@sm/utils';
import { Container, Typography } from '@sm/wds-react';
import { createSurveyStyles } from '@sm/webassets';
import { evaluateTextColorVisibility, getThemeColors } from '~app/pages/EnterpriseThankYou/helpers/themeColors';
import { PageVariants, EnterpriseThankYouContext } from '../..';
import './enterprise-thank-you-body.scss';

const useStyles = createSurveyStyles(theme => {
  const { headerTextColor, bodyTextColor, backgroundColor } = getThemeColors(theme);
  const visibleColor = evaluateTextColorVisibility(bodyTextColor, headerTextColor, backgroundColor);
  return {
    bodyTextStyles: {
      color: visibleColor,
      fontFamily: theme?.questionTitle?.fontFamily,
    },
  };
});

const EnterpriseThankYouBody = ({ bodyImageUrl, bodyText, bodyImageRedirectUrl }) => {
  const { pageVariant } = useContext(EnterpriseThankYouContext);
  const premiumCustom = pageVariant === PageVariants.premiumCustom;
  const [imageLoadSuccess, setImageLoadSuccess] = useState(true);
  const { bodyTextStyles } = useStyles();

  const handleBodyTextClick = e => {
    // If the user has clicked on a hyperlink (a tag) then force it to open the link in a new tab
    if (e.target.tagName === 'A') {
      e.preventDefault();
      window.open(e.target.href, '_blank');
    }
  };

  const BodyText = () => {
    return (
      <If condition={bodyText}>
        <Container className="sm-enterprise-thank-you-body__text" flex flexJustify="center">
          <Typography
            className={`${bodyTextStyles} sm-enterprise-thank-you-body__text-inner`}
            align="center"
            color="dark"
            onClick={handleBodyTextClick}
            dangerouslySetInnerHTML={{ __html: sanitizeString(bodyText) }}
          />
        </Container>
      </If>
    );
  };

  const BodyImage = () => {
    return (
      <Choose>
        <When condition={bodyImageUrl && imageLoadSuccess}>
          <Container className="sm-enterprise-thank-you-body__image" flex flexJustify="center">
            <a rel="noopener noreferrer" target="_blank" {...(bodyImageRedirectUrl && { href: bodyImageRedirectUrl })}>
              <img
                data-testid="body__Image"
                className={`sm-enterprise-thank-you-body__inner-image ${
                  bodyImageRedirectUrl
                    ? 'sm-enterprise-thank-you-body__cursor-pointer'
                    : 'sm-enterprise-thank-you-body__cursor-non-pointer'
                }`}
                src={bodyImageUrl}
                alt=""
                onError={() => setImageLoadSuccess(false)}
              />
            </a>
          </Container>
        </When>
        {/* For the case where the image doesn't load, but there needs to be a redirect URL, we just display the URL by itself */}
        <When condition={bodyImageRedirectUrl && !imageLoadSuccess}>
          <Container className="sm-enterprise-thank-you-body__image-redirect-text" flex flexJustify="center">
            <a rel="noopener noreferrer" target="_blank" href={bodyImageRedirectUrl}>
              <Typography
                className={`${bodyTextStyles} sm-enterprise-thank-you-body__redirect-text sm-enterprise-thank-you-body__cursor-pointer`}
                align="center"
                color="dark"
              >
                {bodyImageRedirectUrl}
              </Typography>
            </a>
          </Container>
        </When>
      </Choose>
    );
  };

  return (
    <If condition={premiumCustom}>
      <Container className="sm-enterprise-thank-you__body-outline">
        <BodyText />
        <BodyImage />
      </Container>
    </If>
  );
};

EnterpriseThankYouBody.propTypes = {
  bodyImageUrl: PropTypes.string,
  bodyText: PropTypes.string,
  bodyImageRedirectUrl: PropTypes.string,
};

EnterpriseThankYouBody.defaultProps = {
  bodyImageUrl: null,
  bodyText: null,
  bodyImageRedirectUrl: null,
};

export default EnterpriseThankYouBody;
